// components/data-sources/connectorCatalog/catalogGroups.ts
/**
 * The groups the source picker sorts its tiles into (plan
 * 2026-10-13-connector-catalog-on-request, steps 0.4 and 0.6): ~55 tiles do
 * not fit one flat grid.
 *
 * WHICH group a connector is in comes from the server: `catalog_group` on the
 * connectors endpoint, declared per connector in the backend registry. This
 * file owns only presentation: each group's label and the order the groups
 * render in. `GROUP_LABEL` is a `Record` over the generated union, so a group
 * the backend adds fails `tsc` here until it has a label.
 *
 * "other" is not a registry value. It is the fallback for a connector the
 * server sends without a group (a backend that predates the field, mid-deploy)
 * or with a group this bundle does not know yet (a newer backend).
 */

import type { components } from "@verity/shared-types";

import type { ManagedSourceConnector } from "@/types/managedSources";

/** A group the backend registry can declare. */
export type ServerCatalogGroup = components["schemas"]["ConnectorInfo"]["catalog_group"];

export type CatalogGroupKey = ServerCatalogGroup | "other";

export const GROUP_LABEL: Readonly<Record<CatalogGroupKey, string>> = {
  advertising: "Advertising",
  analytics: "Web analytics",
  email_crm: "Email & CRM",
  shop: "Shop platforms",
  marketplaces: "Marketplaces",
  payments: "Payments",
  shipping: "Shipping",
  fulfilment: "Inventory & fulfilment",
  finance: "Finance & accounting",
  product_feeds: "Product feeds",
  service_reviews: "Service & reviews",
  files: "Files",
  warehouses: "Databases & warehouses",
  other: "Other",
};

/** Display order. Every key of `GROUP_LABEL` appears once (pinned by a test). */
export const CATALOG_GROUP_ORDER: ReadonlyArray<CatalogGroupKey> = [
  "advertising",
  "analytics",
  "email_crm",
  "shop",
  "marketplaces",
  "payments",
  "shipping",
  "fulfilment",
  "finance",
  "product_feeds",
  "service_reviews",
  "files",
  "warehouses",
  "other",
];

/** Display order and label of every group. */
export const CATALOG_GROUPS: ReadonlyArray<{ key: CatalogGroupKey; label: string }> =
  CATALOG_GROUP_ORDER.map((key) => ({ key, label: GROUP_LABEL[key] }));

function isKnownGroup(value: unknown): value is CatalogGroupKey {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(GROUP_LABEL, value);
}

/** The group the server placed this connector in; "other" when it named none we know. */
export function groupForConnector(
  connector: Pick<ManagedSourceConnector, "catalog_group">,
): CatalogGroupKey {
  return isKnownGroup(connector.catalog_group) ? connector.catalog_group : "other";
}

export function groupLabel(key: CatalogGroupKey): string {
  return GROUP_LABEL[key] ?? GROUP_LABEL.other;
}
